import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import type { TouchpointSourceType } from '@trello/ad-sequencer';
import { Analytics } from '@trello/atlassian-analytics';
import { isDesktop } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import {
  type ExperimentVariations,
  useFeatureGate,
  useGetExperimentValue,
} from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';

import { getAvailableSites } from '../getAvailableSites';
import {
  type TrelloCrossFlowExperiment,
  trelloCrossFlowOpen,
} from '../TrelloCrossFlow';
import { isSourceTargetedByFeatureKey } from './isSourceTargetedByFeatureFlag';
import { useEligibleWorkspacesForProvisioning } from './useEligibleWorkspacesForProvisioning';
const oneTimeMessageSources: Partial<Record<TouchpointSourceType, string>> = {
  cardBackDatePicker: '1p-discovery-ad-card-date-range-picker',
  boardDirectory: '1p-discovery-ad-powerup-directory',
  createBoardInlineDialog: '1p-discovery-ad-left-hand-nav-board-create',
  memberHomeWorkspaceSidebarSection: '1p-discovery-ad-left-hand-nav-board-home',
};
const experimentFeatureKey = 'trello_xf_paid_workspaces_ads';

export type CrossFlowForPaidWorkspacesExperimentVariations =
  ExperimentVariations<typeof experimentFeatureKey, 'cohort'>;

export interface CrossFlowForPaidWorkspacesParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
}
export const useCrossFlowForPaidWorkspaces = ({
  source,
  workspaceId,
  boardId,
}: CrossFlowForPaidWorkspacesParams): TrelloCrossFlowExperiment<CrossFlowForPaidWorkspacesExperimentVariations> => {
  const crossFlow = useCrossFlow();

  const [loadingAvailableSites, setLoadingAvailableSites] = useState(false);

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();
  const [hasValidAaSession] = useHasValidAaSession();

  const isSourceOneTimeMessageDismissed = useCallback(
    () => isOneTimeMessageDismissed(oneTimeMessageSources?.[source] || ''),
    [source, isOneTimeMessageDismissed],
  );

  const [fireExposureEvent, setFireExposureEvent] = useState(false);

  const { cohort } = useGetExperimentValue({
    experimentName: experimentFeatureKey,
    parameter: 'cohort',
    fireExposureEvent,
  });

  const eligibleWorkspaceOptions = useEligibleWorkspacesForProvisioning({
    workspaceId,
    entitlementRequired: 'standardOrPremium',
  });

  const [siteEligibility, setSiteEligibility] = useState(false);

  const { value: doSendOperationalEvent } = useFeatureGate(
    'trello_xf_experiment_analytics_toggle',
  );

  const analyticsContainers = useMemo(
    () => ({
      organization: {
        id: workspaceId,
      },
      board: {
        id: boardId,
      },
    }),
    [workspaceId, boardId],
  );

  const sendOperationalEvent = useCallback(
    (operationalEventFn: () => void) => {
      if (doSendOperationalEvent) {
        operationalEventFn();
      }
    },
    [doSendOperationalEvent],
  );

  const isTargeted = useMemo(() => {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'invoked',
        actionSubject: 'getIsTargeted',
        source,
        containers: analyticsContainers,
        attributes: {
          preliminaryCohortValue: cohort,
          isTargeted: cohort !== 'not-enrolled',
          experimentFeatureKey,
        },
      });
    });

    return cohort !== 'not-enrolled';
  }, [analyticsContainers, cohort, sendOperationalEvent, source]);

  const getIsTargeted = useCallback(() => {
    return isTargeted;
  }, [isTargeted]);

  const getExperimentCohort = useCallback(() => {
    if (
      crossFlow.isEnabled &&
      siteEligibility &&
      eligibleWorkspaceOptions.length > 0
    ) {
      sendOperationalEvent(() => {
        Analytics.sendOperationalEvent({
          action: 'invoked',
          actionSubject: 'getExperimentCohort',
          source,
          containers: analyticsContainers,
          attributes: {
            cohort,
            experimentFeatureKey,
          },
        });
      });
      setFireExposureEvent(true);
      return cohort;
    }

    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'invoked',
        actionSubject: 'getExperimentCohort',
        source,
        containers: analyticsContainers,
        attributes: {
          cohort: 'not-enrolled',
          experimentFeatureKey,
        },
      });
    });

    return 'not-enrolled';
  }, [
    analyticsContainers,
    cohort,
    crossFlow.isEnabled,
    eligibleWorkspaceOptions.length,
    sendOperationalEvent,
    siteEligibility,
    source,
  ]);

  const [crossFlowExperiment, setCrossFlowExperiment] = useState<
    TrelloCrossFlowExperiment<CrossFlowForPaidWorkspacesExperimentVariations>
  >({
    open: trelloCrossFlowOpen(
      crossFlow,
      source,
      eligibleWorkspaceOptions,
      hasValidAaSession,
      {
        workspaceId,
        boardId,
      },
    ),
    getIsTargeted,
    getExperimentCohort,
    isEnrolled: false,
    experimentName: experimentFeatureKey,
  });

  useEffect(() => {
    const loadSiteEligibility = async () => {
      try {
        setLoadingAvailableSites(true);
        const { sites } = await getAvailableSites();
        const hasNoSites =
          sites?.length > 0 &&
          sites?.filter(({ cloudId }) => cloudId !== 'TRELLO').length === 0;
        setSiteEligibility(hasNoSites);
      } finally {
        setLoadingAvailableSites(false);
      }
    };

    if (
      !isDesktop() &&
      !loadingAvailableSites &&
      crossFlow.isEnabled &&
      isSourceTargetedByFeatureKey({
        featureKey: experimentFeatureKey,
        source,
      }) &&
      getIsTargeted() &&
      !isSourceOneTimeMessageDismissed()
    ) {
      loadSiteEligibility();
      sendOperationalEvent(() => {
        Analytics.sendOperationalEvent({
          action: 'loaded',
          actionSubject: 'availableSites',
          source,
          containers: analyticsContainers,
          attributes: {
            preliminaryCohortValue: cohort,
            experimentFeatureKey,
          },
        });
      });
    }
    // Intentionally ignoring loadingAvailableSites dependency
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [
    isSourceOneTimeMessageDismissed,
    source,
    workspaceId,
    crossFlow.isEnabled,
    getIsTargeted,
    analyticsContainers,
    cohort,
    sendOperationalEvent,
  ]);

  useEffect(() => {
    if (
      crossFlow.isEnabled &&
      siteEligibility &&
      eligibleWorkspaceOptions.length > 0
    ) {
      sendOperationalEvent(() => {
        Analytics.sendOperationalEvent({
          action: 'passed',
          actionSubject: 'experimentRuntimeCheck',
          source,
          containers: analyticsContainers,
          attributes: {
            preliminaryCohortValue: cohort,
            experimentFeatureKey,
          },
        });
      });

      setCrossFlowExperiment({
        isEnrolled: true,
        open: trelloCrossFlowOpen(
          crossFlow,
          source,
          eligibleWorkspaceOptions,
          hasValidAaSession,
          {
            workspaceId,
            boardId,
          },
        ),
        getIsTargeted,
        getExperimentCohort,
        experimentName: experimentFeatureKey,
      });
    }
    // Intentionally ignoring the crossFlow dependency
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [
    sendOperationalEvent,
    siteEligibility,
    eligibleWorkspaceOptions,
    source,
    workspaceId,
    boardId,
    getExperimentCohort,
    getIsTargeted,
    isSourceOneTimeMessageDismissed,
    analyticsContainers,
    cohort,
  ]);

  useEffect(() => {
    setCrossFlowExperiment((c) => ({
      ...c,
      getIsTargeted,
    }));
  }, [getIsTargeted]);

  return crossFlowExperiment;
};
